import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridFilterModel } from '@mui/x-data-grid-pro';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { ClientFormDataWithId } from '../pages/CreateNewClient/CreateNewClientUtils';
import { TemplateFormData } from '../pages/TemplateCenter/CreateNewTemplate/NewTemplateUtils';
import { FormData as TaskFormData } from '../pages/EditTask/EditTaskProps';
import {
  errorsProps,
  warningsProps,
} from '../components/SummaryErrors/SummaryErrors';
import { GetTemplateResponse } from '../apiClient/templates';
import { TaskCreatorFormValues } from '../pages/HomePage/components/HomePageTaskCreator/HomePageTaskCreatorUtils';
import { authAtom } from './authAtom';

export type EditClientErrors = {
  backendErrors: errorsProps[];
  mergedErrors: errorsProps[];
  warnings?: warningsProps[];
};

export type TemplateData = {
  localTemplate: GetTemplateResponse;
  templateInDatabase: GetTemplateResponse;
};

type MainTabUrl = string;
export interface MainTab {
  name: string;
  isDirty: boolean;
  url: MainTabUrl;
  clientFormData?: ClientFormDataWithId;
  templateFormData?: TemplateFormData;
  templateOriginalFormData?: TemplateFormData;
  taskFormData?: TaskFormData;
  taskOriginalFormData?: TaskFormData;
  taskSubTab?: string;
  editClientErrors?: EditClientErrors;
  templateData?: TemplateData;
  filingTaxPayments?: Record<number, GetTemplateResponse>;
  data?: unknown;
  errors?: { formattedErrors: errorsProps[]; backendErrors: errorsProps[] };
}

export const HOME_TAB_URL: MainTabUrl = '/';

export type HomeTaskCreatorSnapshot = {
  selectedTypeName: string | null;
  isExpanded: boolean;
  formValues: TaskCreatorFormValues;
  formattedErrors: errorsProps[];
};

export type HomeTasksFiltersSnapshot = {
  filterModel: GridFilterModel;
  dueBy: string;
  date: string | null;
};

export type HomeSidebarTab = 'tasks' | 'clients' | 'live-log';

export type HomeLiveLogFiltersSnapshot = {
  since: string | null;
  assignedTo: string | null;
  by: string | null;
  clientId: string | null;
  actions: number[];
};

const visitedTabsAtom = atomWithStorage<MainTabUrl[]>(
  'visitedTabs',
  [HOME_TAB_URL],
  createJSONStorage(() => sessionStorage)
);

const tabsAtom = atomWithStorage<MainTab[]>(
  'tabs',
  [],
  createJSONStorage(() => sessionStorage)
);

const homeTaskCreatorAtom = atomWithStorage<
  HomeTaskCreatorSnapshot | undefined
>(
  'homeTaskCreator',
  undefined,
  createJSONStorage(() => sessionStorage)
);

type SnapshotsByUser<T> = Record<number, T>;

const homeTasksFiltersAtom = atomWithStorage<
  SnapshotsByUser<HomeTasksFiltersSnapshot>
>(
  'homeTasksFiltersByUser',
  {},
  createJSONStorage(() => sessionStorage)
);

const homeLiveLogFiltersAtom = atomWithStorage<
  SnapshotsByUser<HomeLiveLogFiltersSnapshot>
>(
  'homeLiveLogFiltersByUser',
  {},
  createJSONStorage(() => sessionStorage)
);

const withUserSnapshot = <T>(
  snapshots: SnapshotsByUser<T>,
  userId: number,
  snapshot: T | undefined
): SnapshotsByUser<T> =>
  Object.fromEntries([
    ...Object.entries(snapshots).filter(([id]) => Number(id) !== userId),
    ...(snapshot === undefined ? [] : [[userId, snapshot]]),
  ]);

const homeSidebarTabAtom = atomWithStorage<HomeSidebarTab>(
  'homeSidebarTab',
  'tasks',
  createJSONStorage(() => sessionStorage)
);

function sanitizeTabs(tabs: MainTab[]): MainTab[] {
  return tabs.filter((tab) => tab.url !== HOME_TAB_URL && Boolean(tab.name));
}

let homeTabData: unknown;

export function useTabNavigation() {
  const navigate = useNavigate();
  const [rawTabs, setTabs] = useAtom(tabsAtom);
  const [visitedTabs, setVisitedTabs] = useAtom(visitedTabsAtom);
  const [homeTaskCreatorSnapshot, setHomeTaskCreatorSnapshotAtom] =
    useAtom(homeTaskCreatorAtom);
  const userId = useAtomValue(authAtom).user?.id;
  const [homeTasksFiltersByUser, setHomeTasksFiltersByUser] =
    useAtom(homeTasksFiltersAtom);
  const [homeLiveLogFiltersByUser, setHomeLiveLogFiltersByUser] = useAtom(
    homeLiveLogFiltersAtom
  );
  const [homeSidebarTab, setHomeSidebarTab] = useAtom(homeSidebarTabAtom);
  const tabs = useMemo(() => sanitizeTabs(rawTabs), [rawTabs]);

  useEffect(() => {
    if (tabs.length !== rawTabs.length) {
      setTabs(tabs);
    }
  }, [tabs, rawTabs, setTabs]);

  function openTab(tab: MainTab) {
    if (tab.url === HOME_TAB_URL || !tab.name) {
      makeTabActive(tab.url);
      return;
    }

    setTabs((tabs) => {
      const index = tabs.findIndex((t) => t.url === tab.url);
      if (index === -1) return [...tabs, tab];

      const newTabs = [...tabs];
      newTabs[index] = tab;
      return newTabs;
    });

    setVisitedTabs((tabs) => {
      const newTabs = tabs.filter((tabUrl) => tabUrl !== tab.url);
      return [...newTabs, tab.url];
    });
    navigate(tab.url);
  }

  function makeTabActive(url: MainTabUrl) {
    setVisitedTabs((tabs) => {
      const newTabs = tabs.filter((tabUrl) => tabUrl !== url);
      return [...newTabs, url];
    });
    navigate(url);
  }

  function openOrActivateTab(tab: MainTab) {
    if (isTabOpen(tab.url)) {
      makeTabActive(tab.url);
      return;
    }
    openTab(tab);
  }

  function setHomeTabData(data: unknown) {
    homeTabData = data;
  }

  function getHomeTabData<T>(): T | undefined {
    return homeTabData as T | undefined;
  }

  function setHomeTaskCreatorSnapshot(
    snapshot: HomeTaskCreatorSnapshot | undefined
  ) {
    setHomeTaskCreatorSnapshotAtom(snapshot);
  }

  function getHomeTaskCreatorSnapshot(): HomeTaskCreatorSnapshot | undefined {
    return homeTaskCreatorSnapshot;
  }

  function setHomeTasksFilters(snapshot: HomeTasksFiltersSnapshot | undefined) {
    if (userId === undefined) return;
    setHomeTasksFiltersByUser((prev) =>
      withUserSnapshot(prev, userId, snapshot)
    );
  }

  function getHomeTasksFilters(): HomeTasksFiltersSnapshot | undefined {
    return userId === undefined ? undefined : homeTasksFiltersByUser[userId];
  }

  function setHomeLiveLogFilters(
    snapshot: HomeLiveLogFiltersSnapshot | undefined
  ) {
    if (userId === undefined) return;
    setHomeLiveLogFiltersByUser((prev) =>
      withUserSnapshot(prev, userId, snapshot)
    );
  }

  function getHomeLiveLogFilters(): HomeLiveLogFiltersSnapshot | undefined {
    return userId === undefined ? undefined : homeLiveLogFiltersByUser[userId];
  }

  const syncActiveTabToRoute = useCallback(
    (url: MainTabUrl, name: string) => {
      setVisitedTabs((prevVisited) => {
        if (prevVisited[prevVisited.length - 1] === url) return prevVisited;
        const newVisited = prevVisited.filter(
          (visitedUrl) => visitedUrl !== url
        );
        return [...newVisited, url];
      });

      if (url === HOME_TAB_URL) return;

      setTabs((prevTabs) => {
        if (prevTabs.some((tab) => tab.url === url)) return prevTabs;
        return [...prevTabs, { name, isDirty: false, url }];
      });
    },
    [setTabs, setVisitedTabs]
  );

  function removeTab(url: MainTabUrl) {
    setTabs((tabs) => tabs.filter((tab) => tab.url !== url));

    setVisitedTabs((visitedTabs) => {
      const wasActive = visitedTabs[visitedTabs.length - 1] === url;
      const newVisitedTabs = visitedTabs.filter(
        (visitedUrl) => visitedUrl !== url
      );

      if (wasActive) {
        const newUrl = newVisitedTabs[newVisitedTabs.length - 1] || '/';
        navigate(newUrl);
      }

      return newVisitedTabs;
    });
  }

  function removeInactiveTab(url: MainTabUrl) {
    setTabs((tabs) => tabs.filter((tab) => tab.url !== url));
    setVisitedTabs((tabs) => tabs.filter((tabUrl) => tabUrl !== url));
  }

  function removeAllTabs() {
    setTabs([]);
    setVisitedTabs([HOME_TAB_URL]);
  }

  function isTabOpen(url: MainTabUrl) {
    return tabs.find((t) => t.url === url);
  }

  function getTabData<T>(url: MainTabUrl): T | undefined {
    return tabs.find((t) => t.url === url)?.data as T | undefined;
  }

  function getIsTabDirty(url: MainTabUrl) {
    return tabs.find((t) => t.url === url)?.isDirty;
  }

  function setClientFormData(tabName: string, data: ClientFormDataWithId) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.name === tabName ? { ...tab, clientFormData: data } : tab
      )
    );
  }

  function setTemplateFormData(url: MainTabUrl, data?: TemplateFormData) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url ? { ...tab, templateFormData: data } : tab
      )
    );
  }

  function getTemplateFormData(url: MainTabUrl): TemplateFormData | undefined {
    return tabs.find((t) => t.url === url)?.templateFormData;
  }

  function setTemplateOriginalFormData(
    url: MainTabUrl,
    data?: TemplateFormData
  ) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url ? { ...tab, templateOriginalFormData: data } : tab
      )
    );
  }

  function getTemplateOriginalFormData(
    url: MainTabUrl
  ): TemplateFormData | undefined {
    return tabs.find((t) => t.url === url)?.templateOriginalFormData;
  }

  function setTaskFormData(url: MainTabUrl, data?: TaskFormData) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url ? { ...tab, taskFormData: data } : tab
      )
    );
  }

  function getTaskFormData(url: MainTabUrl): TaskFormData | undefined {
    return tabs.find((t) => t.url === url)?.taskFormData;
  }

  function setTaskOriginalFormData(url: MainTabUrl, data?: TaskFormData) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url ? { ...tab, taskOriginalFormData: data } : tab
      )
    );
  }

  function getTaskOriginalFormData(url: MainTabUrl): TaskFormData | undefined {
    return tabs.find((t) => t.url === url)?.taskOriginalFormData;
  }

  function setErrors(
    url: MainTabUrl,
    errors: { formattedErrors: errorsProps[]; backendErrors: errorsProps[] }
  ) {
    setTabs((tabs) =>
      tabs.map((tab) => (tab.url === url ? { ...tab, errors: errors } : tab))
    );
  }

  function getErrors(
    url: MainTabUrl
  ):
    | { formattedErrors: errorsProps[]; backendErrors: errorsProps[] }
    | undefined {
    return tabs.find((t) => t.url === url)?.errors;
  }

  function updateTabDirtyState({
    tabId,
    isDirty,
  }: {
    tabId: string;
    isDirty: boolean;
  }) {
    setTabs((tabs) => {
      const updatedTabs = tabs.map((tab) =>
        tab.name === tabId ? { ...tab, isDirty } : tab
      );
      return updatedTabs;
    });
  }

  function setTab(url: MainTabUrl, fn: (previousTab: MainTab) => MainTab) {
    setTabs((tabs) => {
      const index = tabs.findIndex((t) => t.url === url);
      if (index === -1) return tabs;
      const currentTab = tabs[index];
      const newTab = fn(currentTab);
      if (newTab === currentTab) return tabs;
      const newTabs = [...tabs];
      newTabs[index] = newTab;
      return newTabs;
    });
  }

  function setEditClientErrors(
    url: MainTabUrl,
    fn: (prev: EditClientErrors | undefined) => EditClientErrors | undefined
  ) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url
          ? { ...tab, editClientErrors: fn(tab.editClientErrors) }
          : tab
      )
    );
  }

  function getEditClientErrors(url: MainTabUrl): EditClientErrors | undefined {
    return tabs.find((t) => t.url === url)?.editClientErrors;
  }

  function setTemplateData(
    url: MainTabUrl,
    fn: (prev: TemplateData | undefined) => TemplateData | undefined
  ) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url ? { ...tab, templateData: fn(tab.templateData) } : tab
      )
    );
  }

  function getTemplateData(url: MainTabUrl): TemplateData | undefined {
    return tabs.find((t) => t.url === url)?.templateData;
  }

  function setFilingTaxPayments(
    url: MainTabUrl,
    fn: (
      prev: Record<number, GetTemplateResponse> | undefined
    ) => Record<number, GetTemplateResponse> | undefined
  ) {
    setTabs((tabs) =>
      tabs.map((tab) =>
        tab.url === url
          ? { ...tab, filingTaxPayments: fn(tab.filingTaxPayments) }
          : tab
      )
    );
  }

  function getFilingTaxPayments(
    url: MainTabUrl
  ): Record<number, GetTemplateResponse> | undefined {
    return tabs.find((t) => t.url === url)?.filingTaxPayments;
  }

  function getAllTabs() {
    return tabs;
  }

  return {
    tabs,
    activeTab: visitedTabs[visitedTabs.length - 1] as MainTabUrl,
    openTab,
    openOrActivateTab,
    makeTabActive,
    syncActiveTabToRoute,
    removeTab,
    removeInactiveTab,
    removeAllTabs,
    isTabOpen,
    getTabData,
    setHomeTabData,
    getHomeTabData,
    setHomeTaskCreatorSnapshot,
    getHomeTaskCreatorSnapshot,
    setHomeTasksFilters,
    getHomeTasksFilters,
    setHomeLiveLogFilters,
    getHomeLiveLogFilters,
    homeSidebarTab,
    setHomeSidebarTab,
    getIsTabDirty,
    setClientFormData,
    setTemplateFormData,
    getTemplateFormData,
    setTemplateOriginalFormData,
    getTemplateOriginalFormData,
    setTaskFormData,
    getTaskFormData,
    setTaskOriginalFormData,
    getTaskOriginalFormData,
    updateTabDirtyState,
    setTab,
    getAllTabs,
    setEditClientErrors,
    getEditClientErrors,
    setTemplateData,
    getTemplateData,
    setFilingTaxPayments,
    getFilingTaxPayments,
    setErrors,
    getErrors,
  };
}
